import React, { useState, useRef } from 'react';
import cn from 'clsx';
import { styled } from 'linaria/react';
import { memo } from 'common/hocs/memo';
import { Pagination } from 'swiper/modules';
import 'swiper/css/pagination';
import { createElementKey } from 'utils';
import { MediaCard } from '../media-card';
import { SwiperClass, SwiperSlide, SwiperWithNavigation, Swiper } from '../swiper';
import {
  cardImage,
  cardImageHoverSwiper,
  cardImageHoverSwiperWrapper,
  cardImageSwiper,
  priceCardDescription,
  priceCardName,
} from './price-media-card-styles';
import { Typography, TypographyProps } from '../typography';

export const PriceMediaCard = styled(MediaCard)`
  align-items: initial;
`;

type PriceMediaCardSwiperProps = {
  landingCdn: string;
  photosSrc: string[];
  photosName: string;
  keyName: string;
};

type HoverPriceMediaCardSwiperProps = {
  photosSrc: string[];
  photosName: string;
  keyName: string;
  imageClass?: string;
};

export const PriceMediaCardSwiper: React.FC<PriceMediaCardSwiperProps> = ({
  landingCdn,
  photosSrc,
  photosName,
  keyName,
}) => (
  <div>
    <SwiperWithNavigation
      modules={[Pagination]}
      slidesPerView="auto"
      watchOverflow
      pagination={{
        clickable: false,
      }}
      className={cardImageSwiper}>
      {photosSrc.map((photo, photoIndex: number) => (
        <SwiperSlide key={createElementKey(keyName, photoIndex)}>
          <img
            className={cardImage}
            src={`${landingCdn}${photo}`}
            title={photosName}
            alt={photosName}
            width="377"
            height="236"
            loading="lazy"
          />
        </SwiperSlide>
      ))}
    </SwiperWithNavigation>
  </div>
);

export const HoverPriceMediaCardSwiper: React.FC<HoverPriceMediaCardSwiperProps> = ({
  photosSrc,
  photosName,
  keyName,
  imageClass,
}) => {
  const [swiper, setSwiper] = useState<SwiperClass | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!containerRef?.current) {
      return;
    }

    const containerRect = containerRef.current.getBoundingClientRect();
    const oneSlidePartWidth = containerRect.width / photosSrc.length;
    const slideNumber = Math.floor((e.pageX - containerRect.left) / oneSlidePartWidth);

    swiper?.slideTo(slideNumber);
  };

  return (
    <div className={cardImageHoverSwiperWrapper} ref={containerRef} onMouseMove={handleMouseMove}>
      <Swiper
        modules={[Pagination]}
        slidesPerView="auto"
        watchOverflow
        pagination={{
          clickable: false,
        }}
        onSwiper={setSwiper}
        className={cardImageHoverSwiper}>
        {photosSrc.map((photo, photoIndex: number) => (
          <SwiperSlide key={createElementKey(keyName, photoIndex)}>
            <img
              className={cn(imageClass, cardImage)}
              src={photo}
              title={photosName}
              alt={photosName}
              width="377"
              height="236"
              loading="lazy"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
};

export const PriceMediaCardContent = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 100%;
  padding: 16px;
`;

type PriceMediaCardTitleProps = {
  as?: keyof React.ReactHTML;
} & Omit<TypographyProps, 'variant' | 'size' | 'as' | 'adaptiveProps'>;

export const PriceMediaCardTitle = memo(({ children, as = 'p', ...rest }: PriceMediaCardTitleProps) => (
  <Typography className={priceCardName} variant="s" size="body_accent" as={as} {...rest}>
    {children}
  </Typography>
));

type PriceMediaCardDescriptionProps = {
  description: string;
};

export const PriceMediaCardDescription: React.FC<PriceMediaCardDescriptionProps> = ({ description }) => (
  <Typography variant="s" size="body_long" color="text-secondary" className={priceCardDescription}>
    {description}
  </Typography>
);
